import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextInputModel } from 'projects/money-movement-ui/src/app/shared/components/controls/text-input-control/TextInputModel';
import { thirdPartyCheckFieldsErrorMap } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Observable, Subject, takeUntil, withLatestFrom, filter, of, take } from 'rxjs';
import { zipCodeAllowedChars, zipCodePattern } from '../../../../shared/utilities/regex-patterns';
import { SelectControl } from '../../../models/OptionDropdownSelect';
import { CodeAndDescription } from '../../../models/shared/CodeAndDescription';
import { createControl } from '../../../utilities/formUtilities';
import { FirmDetails } from '../../../models/check/CheckData';
import { TitleCasePipe } from '@angular/common';
import * as CustomValidators from '../../../../shared/utilities/FormValidators';
import { CheckTransactionDetailsForm, RecipientValues } from '../../../models/form/TransactionFormGroup';

@Component({
  standalone: false,
  selector: 'mm-check-third-party-domestic-fields',
  templateUrl: './check-third-party-domestic-fields.component.html',
  styleUrls: ['./check-third-party-domestic-fields.component.scss']
})
export class CheckThirdPartyDomesticFieldsComponent implements OnInit {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm>;
  @Input() stateList$: Observable<CodeAndDescription[]>;
  @Input() recipientSelection$: Observable<string>;
  @Input() firmAddress: FirmDetails;
  @Input() isFromEdit$: Observable<boolean> = of(false);
  @Input() shouldDisableFieldForEditQcd: boolean = false;
  readonly controlNames = {
    city: 'city',
    state: 'state',
    zipCode: 'zipCode'
  }
  zipCodeAllowedChars = zipCodeAllowedChars;
  cityModel: TextInputModel = {
    controlName: this.controlNames.city,
    label: 'City',
    hasGreyText: false,
    htmlMaxLength: 30
  };
  stateListDropdown: SelectControl = {
    title: 'State',
    controlName: this.controlNames.state,
    options: [],
    validationMessage: 'State is required.',
  };
  zipCodeModel: TextInputModel = {
    controlName: this.controlNames.zipCode,
    label: 'ZIP code',
    hasGreyText: false,
    htmlMaxLength: 10,
    allowedChars: this.zipCodeAllowedChars
  };
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private titleCasePipe: TitleCasePipe) { }

  ngOnInit(): void {
    createControl(this.parentForm, this.controlNames.city, '', [Validators.required, CustomValidators.isEmptyString], undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.state, '', [Validators.required, CustomValidators.isEmptyString], undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.zipCode, '', [Validators.required, Validators.pattern(zipCodePattern)], undefined, this.shouldDisableFieldForEditQcd);

    // When recipient type is advisoryFirm and NOT edit flow, set the form values to the firm address
    this.recipientSelection$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.isFromEdit$),
      filter(([recipientType, isFromEdit]) => recipientType === 'advisoryFirm' && !isFromEdit)
    ).subscribe(() => {
      this.setFirmAddressFields(this.firmAddress);
    })

    // When recipient selection changes, reset all fields except for when recipient is advisoryFirm
    this.recipient?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter((recipientType) => recipientType !== 'advisoryFirm')
    ).subscribe(() => {
      Object.keys(this.controlNames).forEach((name) => {
        const names = this.controlNames;
        this.parentForm?.get(this.controlNames[name as keyof typeof names])?.reset();
      });
    })

    this.stateList$.pipe(take(1)).subscribe(stateList => {
      this.stateListDropdown.options = stateList.map((state) => {
        return { value: state.code, name: state.code };
      });
    });
  }



  ngOnDestroy() {
    if (this.city) this.parentForm.removeControl('city');
    if (this.state) this.parentForm.removeControl('state');
    if (this.zipCode) this.parentForm.removeControl('zipCode');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get zipCode(): FormControl<string> {
    return this.parentForm.get(this.controlNames.zipCode) as FormControl<string>;
  }
  get city(): FormControl<string> {
    return this.parentForm.get(this.controlNames.city) as FormControl<string>;
  }
  get state(): FormControl<string> {
    return this.parentForm.get(this.controlNames.state) as FormControl<string>;
  }
  get cityError(): string {
    const error = this.mapErrors(this.cityModel.label, this.city);
    return error === undefined ? '' : error;
  }
  get zipCodeError(): string {
    const error = this.mapErrors(this.zipCodeModel.label, this.zipCode);
    return error === undefined ? '' : error;
  }
  get recipient(): FormControl<typeof RecipientValues[number]> {
    return this.parentForm.get('recipient') as FormControl<typeof RecipientValues[number]>;
  }

  private mapErrors(
    name: string,
    control: FormControl | undefined
  ): string | undefined {
    const errorMap = thirdPartyCheckFieldsErrorMap(name);
    const errorKeys = Object.keys(control?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof thirdPartyCheckFieldsErrorMap];
    }

    return '';
  }

  private setFirmAddressFields(firmDetails: FirmDetails): void {
    this.city.setValue(this.titleCasePipe.transform(firmDetails.city));
    this.state.setValue(firmDetails.state);
    this.zipCode.setValue(firmDetails.zip);
  }
}
